/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * AX1 LAYOUT TOKENS — Premium Enterprise Layout System
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for all layout dimensions in the application.
 * Import these constants instead of hardcoding pixel values.
 *
 * Design Principles:
 * - Consistent gutters across all viewport sizes
 * - Predictable responsive behavior
 * - Premium enterprise density (Stripe/Linear/Vercel style)
 * - Zero visual jank during resize/navigation
 */

// ═══════════════════════════════════════════════════════════════════════════════
// APP SHELL DIMENSIONS
// ═══════════════════════════════════════════════════════════════════════════════

/** Header height: 64px (h-16) */
export const HEADER_HEIGHT = 64;
export const HEADER_HEIGHT_CLASS = "h-16";

/** Sidebar widths */
export const SIDEBAR_WIDTH_EXPANDED = 240; // w-60
export const SIDEBAR_WIDTH_COLLAPSED = 64; // w-16
export const SIDEBAR_WIDTH_HIDDEN = 0;

export const SIDEBAR_WIDTH_EXPANDED_CLASS = "w-60";
export const SIDEBAR_WIDTH_COLLAPSED_CLASS = "w-16";

/** Mobile breakpoint: 1024px (lg) */
export const MOBILE_BREAKPOINT = 1024;

/**
 * Frame for shell wrappers that sit between Layout's `<main>` and a `<Page>`
 * (portal guards, portal surface adapters).
 *
 * `<main>` is a fixed-height `overflow-hidden` flex column and every `<Page>`
 * variant is `h-full` — so any intermediate element with auto height breaks the
 * percentage-height chain and the page's own scroll container never receives a
 * bounded height. `min-h-0` is required because a flex item's default
 * `min-height: auto` would otherwise let content push the wrapper past 100%.
 */
export const SHELL_FRAME_CLASS = "h-full min-h-0 flex flex-col";

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE PADDING TOKENS (Legacy - preserved for compatibility)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Horizontal padding by viewport:
 * - Mobile (<1024px): 16px (px-4)
 * - Desktop (≥1024px): 24px (px-6)
 * @deprecated Use PAGE.padX instead
 */
export const PAGE_PADDING_X_MOBILE = 16; // px-4
export const PAGE_PADDING_X_DESKTOP = 24; // px-6
export const PAGE_PADDING_X_CLASS = "px-4 sm:px-6 lg:px-8";

/**
 * Vertical padding:
 * - Default density: 16px (py-4)
 * - Compact density: 12px (py-3)
 * @deprecated Use PAGE.padY instead
 */
export const PAGE_PADDING_Y_DEFAULT = 16; // py-4
export const PAGE_PADDING_Y_COMPACT = 12; // py-3
export const PAGE_PADDING_Y_CLASS = "py-4";
export const PAGE_PADDING_Y_COMPACT_CLASS = "py-3";

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE TOKENS — Global Layout Contract (matches Admin Dashboard / Command Center)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Single source of truth for page-level layout dimensions.
 * All pages MUST use these tokens to ensure visual consistency.
 *
 * Golden Reference: Admin Dashboard / Command Center
 * - application measure: fluid content capped at 1800px
 * - outer gutter: 16px → 24px → 32px responsive (px-4 sm:px-6 lg:px-8)
 * - frameY: stable responsive page inset (py-4 / py-5 / py-6)
 *
 * Page geometry must not move when a user changes control density. Density is
 * still available to component internals, but every role and portal shares the
 * same route-frame position.
 */
export const PAGE = {
  /** Default application measure: fluid up to 1800px */
  maxWidth: "max-w-[1800px]",

  /** Horizontal padding responsive: px-4 sm:px-6 lg:px-8 */
  padX: "px-4 sm:px-6 lg:px-8",

  /** Stable vertical route-frame inset: 16px -> 20px -> 24px */
  frameY: "py-4 sm:py-5 lg:py-6",

  /** Vertical padding by density mode */
  padY: {
    compact: "py-4",
    comfortable: "py-6",
    spacious: "py-8",
  },

  /** Full padding by density mode (padX + padY combined) */
  padding: {
    compact: "px-4 sm:px-6 lg:px-8 py-4",
    comfortable: "px-4 sm:px-6 lg:px-8 py-6",
    spacious: "px-4 sm:px-6 lg:px-8 py-8",
  },

  /** Chrome/toolbar background - uses AX1 app bg with subtle radial depth */
  chromeBg: "bg-app",

  /** Subtle section background for optional emphasis */
  subtleBg: "bg-muted/20",

  /** Centered application container helper: fluid up to 1800px */
  container: "max-w-[1800px] mx-auto",

  /** @deprecated Page owns gutters outside the measured frame. */
  containerPadded: "max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// AX1 SURFACE TOKENS — Premium Enterprise Design System (2026)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * AX1 surface tokens for premium glass chrome and unified styling.
 * Single source of truth for app background, header, tabs, and surfaces.
 *
 * Design Language:
 * - Clean, technical, high-precision
 * - Cool neutral background (#F6F8FB)
 * - Glass chrome (white/75 +)
 * - Minimal shadows, strong spacing rhythm
 */
export const AX1 = {
  /** Layer 0: App background - cool neutral with radial atmosphere (via CSS) */
  appBg: "bg-app",

  /** Layer 1: Glass header background */
  headerBg:
    "bg-card/75 backdrop-saturate-150",

  /** Header border */
  headerBorder: "border-b-2 border-border/50",

  /** Header shadow on scroll (micro-shadow) */
  headerShadowOnScroll: "shadow-[0_1px_0_rgba(0,0,0,0.04)]",

  /** Layer 2: Tab rail background - lighter than header for depth */
  tabRailBg: "bg-card/60",

  /** Tab rail border: softer than header */
  tabRailBorder: "border-b border-border/40",

  /** Tab indicator: 2px underline in primary */
  tabIndicator: "h-0.5 bg-primary-500 rounded-full",

  /** Tab text states — refined for calm hierarchy */
  tabText: {
    inactive: "text-muted-foreground",
    hover: "text-foreground",
    active: "text-foreground font-medium",
  },

  /** Surface radius — AX1 Global Radius System (ClickUp-inspired, Enterprise-calibrated)
   *
   * | Component          | Radius        | Token Key     | Value  |
   * |--------------------|---------------|---------------|--------|
   * | Inner / Dense UI   | rounded-xs    | inner         | 8px    |
   * | Table cells        | rounded-xs    | tableInner    | 8px    |
   * | Inputs / Selects   | rounded-[var(--radius-xs)] | input         | 8px    |
   * | Buttons            | rounded-[var(--radius-xs)] | button        | 8px    |
   * | Controls / Filters | rounded-[var(--radius-xs)] | control       | 8px    |
   * | Tooltips           | rounded-xs    | tooltip        | 8px    |
   * | List items (inner) | rounded-xs    | listItem      | 8px    |
   * | Compact cards      | rounded-[var(--radius-xs)] | cardCompact   | 8px    |
   * | Standard cards     | rounded-[var(--radius-sm)] | card          | 12px   |
   * | Table wrappers     | rounded-[var(--radius-sm)] | tableWrapper  | 12px   |
   * | Dropdowns          | rounded-[var(--radius-sm)] | dropdown      | 12px   |
   * | Panels / Sections  | rounded-[var(--radius-lg)] | panel         | 16px   |
   * | Containers         | rounded-[var(--radius-lg)] | container     | 16px   |
   * | Modals / Drawers   | rounded-[var(--radius-lg)] | modal         | 16px   |
   * | Overlays           | rounded-[var(--radius-lg)] | overlay       | 16px   |
   * | Chips / Tags       | rounded-pill  | chip          | 999px  |
   * | Tabs (segmented)   | rounded-pill  | tab           | 999px  |
   * | Status badges      | rounded-pill  | badge         | 999px  |
   * | Avatars            | rounded-full  | avatar        | 9999px |
   * | Progress bars      | rounded-full  | progress      | 9999px |
   */
  radius: {
    inner: "rounded-xs",
    tableInner: "rounded-xs",
    control: "rounded-[var(--radius-xs)]",
    input: "rounded-[var(--radius-xs)]",
    button: "rounded-[var(--radius-xs)]",
    tooltip: "rounded-xs",
    listItem: "rounded-xs",
    cardCompact: "rounded-[var(--radius-xs)]",
    card: "rounded-[var(--radius-sm)]",
    tableWrapper: "rounded-[var(--radius-sm)]",
    dropdown: "rounded-[var(--radius-sm)]",
    surface: "rounded-[var(--radius-lg)]",
    panel: "rounded-[var(--radius-lg)]",
    container: "rounded-[var(--radius-lg)]",
    modal: "rounded-[var(--radius-lg)]",
    overlay: "rounded-[var(--radius-lg)]",
    chip: "rounded-pill",
    tab: "rounded-pill",
    badge: "rounded-pill",
    pill: "rounded-pill",
    avatar: "rounded-full",
    progress: "rounded-full",
  },

  /** Layer 3: Card surface — default (static, no hover) */
  card: "rounded-[var(--radius-sm)] bg-[var(--surface-card)] border-0 [box-shadow:var(--surface-shadow)]",

  /** Card surface — interactive (quiet border-led lift) */
  cardInteractive: "ax-surface ax-surface--interactive",

  /** Featured content card — layered depth + interactive lift (list/grid entries) */
  cardPremium: "ax-surface ax-surface--premium ax-surface--interactive",

  /** Card surface — border only (minimal, for nested contexts) */
  cardBordered: "rounded-[var(--radius-sm)] bg-[var(--surface-card)] border border-[color:var(--surface-border)]",

  /** Section spacing (16px minimum vertical) */
  sectionGap: "gap-4",

  /** Block spacing (24px for major sections) */
  blockGap: "gap-6",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// BUTTON TOKENS — Canonical Button System
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Single source of truth for Button styling across the entire application.
 *
 * Design Principles:
 * - Consistent controlled radius per AX1 brand
 * - Brand blue is reserved for trusted primary action
 * - Strict size scale: sm/md/lg
 * - Semantic variants: primary, secondary, outline, ghost, destructive, success, accent
 */
export const BUTTON_TOKENS = {
  /** Base styles applied to all buttons */
  base: "ax-action inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium select-none disabled:cursor-not-allowed disabled:opacity-50",

  /** Variant styles */
  variants: {
    primary:
      "ax-action--primary border border-transparent bg-accent-primary text-action-primary hover:bg-accent-hover active:bg-accent-hover/90",
    default:
      "ax-action--primary border border-transparent bg-accent-primary text-action-primary hover:bg-accent-hover active:bg-accent-hover/90",
    secondary:
      "border border-[color:var(--control-border)] bg-surface-card text-text-primary hover:border-[color:var(--control-border-hover)] hover:bg-[var(--ax-surface-hover)]",
    outline:
      "border border-[color:var(--control-border)] bg-transparent text-text-secondary hover:border-[color:var(--control-border-hover)] hover:bg-[var(--ax-surface-hover)] hover:text-text-primary",
    ghost:
      "border border-transparent bg-transparent text-text-secondary hover:border-[color:var(--ax-border-hairline)] hover:bg-[var(--ax-surface-hover)] hover:text-text-primary",
    destructive:
      "border border-transparent bg-status-danger text-text-inverse hover:bg-status-danger/90 active:bg-status-danger/80",
    'destructive-outline':
      "border border-status-danger/35 bg-transparent text-status-danger-foreground hover:bg-status-danger-soft",
    'destructive-ghost':
      "border border-transparent bg-transparent text-status-danger-foreground hover:border-status-danger/20 hover:bg-status-danger-soft",
    success:
      "border border-status-success/25 bg-status-success-soft text-status-success-foreground hover:border-status-success/35 hover:bg-status-success/15",
    accent:
      "ax-action--primary border border-transparent bg-accent-primary text-action-primary hover:bg-accent-hover active:bg-accent-hover/90",
    link:
      "border border-transparent bg-transparent text-accent-primary underline-offset-4 hover:underline",
  } as const,

  /** Size scale: strict sm/md/lg */
  sizes: {
    sm: "h-8 px-3 text-sm",
    md: "h-9 px-4 text-sm",
    lg: "h-11 px-5 text-base",
    icon: "h-9 w-9 p-0",
    'icon-sm': "h-8 w-8 p-0",
    'icon-md': "h-9 w-9 p-0",
    'icon-touch': "h-11 w-11 p-0",
  } as const,
} as const;

export type ButtonVariant = keyof typeof BUTTON_TOKENS.variants;
export type ButtonSize = keyof typeof BUTTON_TOKENS.sizes;

// ═══════════════════════════════════════════════════════════════════════════════
// INPUT TOKENS — Canonical Input / Textarea System
// ═══════════════════════════════════════════════════════════════════════════════

export const INPUT_TOKENS = {
  /** Base styles for text inputs and textareas */
  base: "ax-control flex w-full border border-[color:var(--control-border)] bg-surface-card px-3 py-2 text-sm text-text-primary placeholder:text-text-muted hover:border-[color:var(--control-border-hover)] focus-visible:border-accent-primary/60 disabled:cursor-not-allowed disabled:opacity-50",

  /** Size scale */
  sizes: {
    sm: "h-8 text-sm",
    md: "h-10 text-sm",
    lg: "h-12 text-base",
  } as const,
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// SELECT / DROPDOWN TOKENS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Shared overlay geometry for every anchored dropdown family.
 *
 * The semantic contents may differ (selection, actions, search, calendar), but
 * the floating surface, safe-area, trigger gap and item rhythm must not.
 */
export const DROPDOWN_GEOMETRY = {
  /** Minimum distance from the viewport edge, including drawer viewports. */
  viewportGap: 12,
  /** Visual separation between trigger and overlay. */
  triggerGap: 8,
  /** Practical ceiling before the panel becomes internally scrollable. */
  maxPanelHeight: 320,
  selectWidth: 280,
  comboboxWidth: 320,
  menuWidth: 224,
  calendarWidth: 256,
} as const;

export const DROPDOWN_TOKENS = {
  /** Opaque institutional overlay with a bounded, native-feeling scroll area. */
  panel:
    "ax-menu-surface z-[200] min-w-[10rem] max-w-[calc(100vw-1.5rem)] overflow-y-auto overscroll-contain p-1.5 scrollbar-thin scrollbar-thumb-border-control scrollbar-track-transparent",
  /** Standard 36px option row; descriptions may naturally increase its height. */
  option:
    "ax-menu-item flex min-h-9 w-full items-center gap-2 px-3 py-2 text-sm cursor-pointer",
  optionDefault: "text-text-secondary",
  optionSelected: "bg-accent-primary/8 font-medium text-accent-primary",
  optionDestructive:
    "ax-menu-item flex min-h-9 w-full items-center gap-2 px-3 py-2 text-sm cursor-pointer text-status-critical-foreground hover:bg-status-critical/5",
  separator: "my-1 border-t border-border-subtle",
  searchHeader:
    "sticky top-0 z-10 -mx-1.5 -mt-1.5 mb-1.5 border-b border-border-subtle bg-surface-card p-2",
  empty: "px-3 py-4 text-center text-sm text-text-muted",
} as const;

export const SELECT_TOKENS = {
  /** Trigger base — shared for all intents */
  triggerBase:
    "ax-control inline-flex items-center justify-between gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed select-none whitespace-nowrap",

  /** Intent styles — visual context for the trigger */
  intents: {
    /** Toolbar / filter-bar filters (compact, border-only) */
    toolbar:
      "border border-[color:var(--control-border)] bg-surface-card text-text-secondary [box-shadow:var(--control-shadow)] hover:bg-surface-muted hover:border-[color:var(--control-border-hover)] hover:[box-shadow:var(--control-shadow-hover)]",
    /** Active toolbar filter (has value selected) */
    "toolbar-active":
      "border border-accent-primary/25 bg-accent-primary/10 text-accent-primary [box-shadow:var(--control-shadow)]",
    /** Form field selects */
    form:
      "border border-[color:var(--control-border)] bg-surface-card text-text-primary [box-shadow:var(--control-shadow)] hover:border-[color:var(--control-border-hover)] hover:[box-shadow:var(--control-shadow-hover)]",
    /** Inline / subtle selects (ghost-like) */
    inline:
      "bg-transparent text-text-secondary hover:bg-surface-muted",
  } as const,

  /** Trigger sizes */
  sizes: {
    sm: "h-9 px-3 text-xs",
    md: "h-10 px-3.5 text-sm",
    lg: "h-11 px-4 text-sm",
  } as const,

  /** Popover panel — depth-2 (floating) */
  panel: DROPDOWN_TOKENS.panel,

  /** Individual option */
  option: DROPDOWN_TOKENS.option,
  optionDefault: DROPDOWN_TOKENS.optionDefault,
  optionSelected: DROPDOWN_TOKENS.optionSelected,

  /** Search input inside panel */
  search:
    "w-full px-3 py-2 text-sm border border-border-subtle rounded-2xl bg-surface-muted text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent-primary/30",
} as const;

export type SelectIntent = keyof typeof SELECT_TOKENS.intents;
export type SelectSize = keyof typeof SELECT_TOKENS.sizes;

// ═══════════════════════════════════════════════════════════════════════════════
// MENU TOKENS
// ═══════════════════════════════════════════════════════════════════════════════

export const MENU_TOKENS = {
  /** Menu panel — depth-2 (floating) */
  panel: DROPDOWN_TOKENS.panel,

  /** Menu item base */
  item: DROPDOWN_TOKENS.option,

  /** Destructive menu item */
  itemDestructive: DROPDOWN_TOKENS.optionDestructive,

  /** Separator */
  separator: DROPDOWN_TOKENS.separator,
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// DENSITY SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Density modes map to AppearanceContext:
 * - 'compact':     Dense operator console (less padding, tighter spacing)
 * - 'comfortable': Default balanced layout (current behavior)
 * - 'spacious':    Relaxed layout with more breathing room
 *
 * Components should use these tokens instead of hardcoding padding/gaps.
 */
export type DensityMode = "compact" | "comfortable" | "spacious";

/**
 * Page-level padding tokens by density mode.
 * Uses PAGE.padX (px-4 sm:px-6 lg:px-8) for horizontal, density-based vertical.
 */
export const DENSITY_PAGE_PADDING = {
  compact: {
    x: PAGE.padX,
    y: PAGE.padY.compact,
    full: PAGE.padding.compact,
  },
  comfortable: {
    x: PAGE.padX,
    y: PAGE.padY.comfortable,
    full: PAGE.padding.comfortable,
  },
  spacious: {
    x: PAGE.padX,
    y: PAGE.padY.spacious,
    full: PAGE.padding.spacious,
  },
} as const;

/**
 * Command bar spacing tokens by density mode.
 * Controls gaps between filters, buttons, and vertical padding.
 */
export const DENSITY_COMMAND_BAR = {
  compact: {
    gap: "gap-2",
    py: "py-2",
    container: "flex items-center gap-2 py-2",
    containerBordered: "flex items-center gap-2 py-2 border-b border-border/60",
  },
  comfortable: {
    gap: "gap-3",
    py: "py-3",
    container: "flex items-center gap-3 py-3",
    containerBordered: "flex items-center gap-3 py-3 border-b border-border/60",
  },
  spacious: {
    gap: "gap-4",
    py: "py-4",
    container: "flex items-center gap-4 py-4",
    containerBordered: "flex items-center gap-4 py-4 border-b border-border/60",
  },
} as const;

/**
 * Vertical spacing tokens by density mode.
 * Use for stacks, section gaps, and list item spacing.
 */
export const DENSITY_SPACING = {
  compact: {
    stack: "space-y-2",
    section: "space-y-4",
    inline: "gap-1.5",
    inlineWide: "gap-2",
  },
  comfortable: {
    stack: "space-y-4",
    section: "space-y-6",
    inline: "gap-2",
    inlineWide: "gap-3",
  },
  spacious: {
    stack: "space-y-6",
    section: "space-y-8",
    inline: "gap-3",
    inlineWide: "gap-4",
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// TAB RAIL TOKENS — Premium Tab Separator System
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Tab Rail: The horizontal line separating tabs from content.
 *
 * Premium Design Principles:
 * - Full-width line extending to page gutters (matches padX)
 * - Subtle color (border/40) for calm hierarchy
 * - Consistent spacing below before content starts
 * - Line sits under tab container, not inside it
 *
 * Usage:
 * <div className={TAB_RAIL.wrapper}>
 *   <TabNavigation ... />
 * </div>
 * <div className={TAB_RAIL.spacingBelow}>
 *   {content}
 * </div>
 */
export const TAB_RAIL = {
  /** Full-width container with rail line: extends to page edges */
  wrapper: "relative",

  /** The separator line: 1px subtle, matches UI.divider */
  line: "border-b border-border/40",

  /** Combined wrapper + line (most common usage) */
  container: "relative border-b border-border/40",

  /** Spacing tokens below the rail before content (padding-top on content area) */
  spacingBelow: {
    compact: "pt-3",
    comfortable: "pt-4",
    spacious: "pt-6",
  },

  /** Margin below the rail element to separate from content below */
  marginBelow: {
    compact: "mb-3",
    comfortable: "mb-4",
    spacious: "mb-6",
  },

  /** Tab container padding (within the rail) */
  tabPadding: {
    compact: "py-1",
    comfortable: "py-2",
    spacious: "py-3",
  },
} as const;

/**
 * Tab size tokens by density mode.
 * Tabs should use smaller sizes in compact mode.
 */
export const DENSITY_TAB_SIZE = {
  compact: "sm",
  comfortable: "md",
  spacious: "md",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// TAB SYSTEM TOKENS — Premium 2026 Tab Navigation
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Tab hierarchy system with two navigation tiers plus a view-mode control:
 *
 * | Level     | Purpose                         | Visual Style                    |
 * |-----------|---------------------------------|---------------------------------|
 * | primary   | Major page or entity sections   | Calm text rail + active marker  |
 * | secondary | Views within the active section | Compact neutral segmented rail  |
 * | mode      | List/grid or display mode        | Tight segmented control         |
 *
 * Use Page.Tabs with `level`. DO NOT create custom tab implementations.
 */
export type TabHierarchy = "primary" | "secondary" | "mode";

/**
 * Canonical tab styling shared by Page.Tabs and UnifiedTabs.
 * Keeping the visual contract here prevents page, drawer, and modal tabs from drifting.
 */
export const TAB_SYSTEM = {
  list: {
    primary: "flex w-full max-w-full items-end gap-1",
    secondary: "inline-flex w-max max-w-full items-center gap-0.5 rounded-lg border border-border-subtle/60 bg-surface-muted/55 p-0.5",
    mode: "inline-flex w-max max-w-full items-center gap-0.5 rounded-lg bg-surface-muted/70 p-0.5",
  },
  tab: {
    base: "relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/35 focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-45",
    size: {
      sm: "h-8 px-2.5 text-xs",
      md: "h-9 px-3 text-sm",
      lg: "h-10 px-4 text-sm",
    },
    primary: {
      base: "rounded-t-md after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:transition-colors after:duration-150",
      active: "font-semibold text-text-primary after:bg-accent-primary",
      inactive: "text-text-muted after:bg-transparent hover:bg-surface-muted/35 hover:text-text-secondary",
    },
    secondary: {
      base: "rounded-md",
      active: "bg-surface-card font-semibold text-text-primary ring-1 ring-border-subtle/70 [box-shadow:var(--control-shadow)]",
      inactive: "text-text-muted hover:bg-surface-chip hover:text-text-secondary",
    },
    mode: {
      base: "rounded-md",
      active: "bg-surface-selected font-semibold text-text-primary [box-shadow:var(--control-shadow)]",
      inactive: "text-text-muted hover:bg-surface-chip hover:text-text-secondary",
    },
  },
  badge: {
    base: "inline-flex min-w-4 items-center justify-center rounded-pill px-1.5 py-0.5 text-[10px] font-semibold tabular-nums transition-colors",
    primaryActive: "bg-accent-primary/10 text-accent-primary",
    secondaryActive: "bg-surface-muted text-text-secondary",
    inactive: "bg-surface-muted/80 text-text-muted",
  },
} as const;

/**
 * Shared portal geometry. Shells own the outer width and height contract;
 * routed pages own only their responsive inner gutter through `<Page>`.
 * Standard list pages use one page scroll owner. A contained scroller is
 * reserved for embedded surfaces such as popovers and drawers.
 */
export const PORTAL_LAYOUT = {
  route: {
    platform: "h-full min-h-0 w-full min-w-0",
    fullBleed: "h-full min-h-0 w-full min-w-0",
  },
  commandRow: "py-2",
  sectionTabs: "px-4 py-2 ax-list-header-band border-b-0",
  tableScroll: "overflow-x-auto overscroll-x-contain scrollbar-on-hover",
  scroll: {
    page: "overflow-visible",
    contained: "max-h-[30rem] overflow-y-auto overscroll-contain scrollbar-on-hover",
  },
} as const;

/**
 * Tab container spacing tokens.
 * Controls padding around tab rows for consistent visual rhythm.
 */
export const TAB_CONTAINER = {
  /** Primary tabs: quiet underline rail for major sections */
  primary: {
    padding: PAGE.padX,
    border: "border-b border-border-subtle/60",
  },
  /** Secondary tabs: inline neutral segmented rail */
  secondary: {
    padding: "p-0.5",
    background: "bg-surface-muted/55",
    radius: "rounded-lg",
  },
  /** Mode controls: tight segmented control */
  mode: {
    padding: "p-0.5",
    background: "bg-surface-muted/70",
    radius: "rounded-lg",
  },
} as const;

/**
 * Tab motion tokens for consistent animations.
 * All tab transitions should use these durations.
 */
export const TAB_MOTION = {
  /** Duration for active indicator movement */
  duration: 150,
  /** Easing function */
  easing: "ease-out",
  /** CSS transition shorthand */
  transition: "transition-all duration-150 ease-out",
} as const;

/**
 * Filter/chip button height tokens by density mode.
 */
export const DENSITY_FILTER_HEIGHT = {
  compact: "h-8",
  comfortable: "h-9",
  spacious: "h-10",
} as const;

/**
 * Helper to map Page's 'default'/'compact' to the global density mode.
 * Page components can use this to bridge their internal types.
 */
export function mapPageDensityToGlobal(
  pageDensity: "default" | "compact",
): DensityMode {
  return pageDensity === "compact" ? "compact" : "comfortable";
}

/**
 * Helper to map global density mode to Page's internal types.
 * Returns 'compact' for compact mode, 'default' for everything else.
 */
export function mapGlobalDensityToPage(
  globalDensity: DensityMode,
): "default" | "compact" {
  return globalDensity === "compact" ? "compact" : "default";
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAX-WIDTH TOKENS (CENTERED LAYOUTS)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Container max-widths for centered content:
 *
 * | Token     | Tailwind      | Pixels  | Use Case                      |
 * |-----------|---------------|---------|-------------------------------|
 * | 4xl       | max-w-4xl     | 896px   | Reading/form focused content  |
 * | 5xl       | max-w-5xl     | 1024px  | Settings panels               |
 * | 6xl       | max-w-6xl     | 1152px  | Wide forms, tables            |
 * | 7xl       | max-w-7xl     | 1280px  | Default centered pages        |
 * | screen-xl | max-w-screen-xl| 1280px | Constrained dashboards        |
 * | screen-2xl| max-w-screen-2xl| 1536px| Spacious app layouts          |
 * | app       | max-w-[1800px]| 1800px  | Standard application frame    |
 * | none      | max-w-none    | ∞       | Canvas/full-width pages       |
 */
export const MAX_WIDTH = {
  "4xl": 896,
  "5xl": 1024,
  "6xl": 1152,
  "7xl": 1280,
  "screen-xl": 1280,
  "screen-2xl": 1536,
  app: 1800,
  premium: 1800, // Command Center width (1800px)
  none: Infinity,
} as const;

export const MAX_WIDTH_CLASSES = {
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  "screen-xl": "max-w-screen-xl",
  "screen-2xl": "max-w-screen-2xl",
  app: "max-w-[1800px]",
  premium: "max-w-[1800px]", // Command Center width
  none: "max-w-none",
} as const;

export type MaxWidthKey = keyof typeof MAX_WIDTH;

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL LAYOUT TOKENS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Standard panel widths for multi-panel layouts:
 *
 * | Token      | Tailwind | Pixels | Use Case                     |
 * |------------|----------|--------|------------------------------|
 * | sidebar-sm | w-60     | 240px  | Narrow tree navigation       |
 * | sidebar-md | w-72     | 288px  | Standard file/folder sidebar |
 * | sidebar-lg | w-80     | 320px  | Wide workspace panels        |
 * | inspector  | w-80     | 320px  | Detail/inspector panels      |
 */
export const PANEL_WIDTH = {
  "sidebar-sm": 240, // w-60
  "sidebar-md": 288, // w-72
  "sidebar-lg": 320, // w-80
  inspector: 320, // w-80
} as const;

export const PANEL_WIDTH_CLASSES = {
  "sidebar-sm": "w-60",
  "sidebar-md": "w-72",
  "sidebar-lg": "w-80",
  inspector: "w-80",
} as const;

export type PanelWidthKey = keyof typeof PANEL_WIDTH;

/**
 * Gap between panels in multi-panel layouts:
 * - Mobile: gap-4 (16px)
 * - Desktop: gap-5 (20px)
 */
export const PANEL_GAP_CLASS = "gap-4 lg:gap-5";

// ═══════════════════════════════════════════════════════════════════════════════
// TRANSITION TOKENS
// ═══════════════════════════════════════════════════════════════════════════════

/** Standard layout transition duration */
export const LAYOUT_TRANSITION_DURATION = 300;
export const LAYOUT_TRANSITION_CLASS =
  "transition-all duration-300 ease-in-out";

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSITE CLASS HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Pre-composed Tailwind class strings for common patterns.
 * Use these for consistency across pages.
 */
export const LAYOUT_CLASSES = {
  /** Full-viewport container that fills available space */
  fullHeight: "h-full w-full overflow-hidden",

  /** Page-level padding (matches Admin Dashboard: px-4 sm:px-6 lg:px-8 py-6) */
  pagePadding: "px-4 sm:px-6 lg:px-8 py-6",

  /** Compact page padding for dense UIs */
  pagePaddingCompact: "px-4 sm:px-6 lg:px-8 py-4",

  /** Spacious page padding */
  pagePaddingSpacious: "px-4 sm:px-6 lg:px-8 py-8",

  /** Centered container with default max-width (1280px) */
  centeredContainer: "max-w-7xl mx-auto",

  /** Stack layout with standard gap */
  stack: "flex flex-col gap-4",

  /** Panels layout (horizontal) */
  panels: "flex h-full w-full gap-4 lg:gap-5 overflow-hidden",

  /** Main content region in panels layout */
  mainPanel: "flex-1 min-w-0 overflow-hidden",

  /** Scrollable content area */
  scrollable: "overflow-y-auto overscroll-contain",

  /** Prevent overflow issues in flex layouts */
  flexSafe: "min-w-0 min-h-0",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE SLOT HEIGHTS (FOR STICKY HEADERS/TABS)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Standard slot heights for page structure:
 * - pageHeader: 56-64px (varies by content)
 * - tabRow: 48px
 * - filterRow: 40-48px
 */
export const SLOT_HEIGHTS = {
  pageHeader: 56, // ~h-14
  tabRow: 48, // h-12
  filterRow: 40, // h-10
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// UI TOKENS — BORDERS, CARDS, SPACING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Unified border/surface tokens for premium consistency.
 * Use these instead of inline border classes.
 *
 * Opacity: 60% for subtle, 100% for prominent
 */
export const UI = {
  /** Standard subtle border: `border border-border/50` */
  border: "border border-border/50",

  /** Prominent border: `border border-border` */
  borderStrong: "border border-border",

  /** Bottom divider line: 1px subtle (matches TAB_RAIL.line) */
  divider: "border-b border-border/40",

  /** Page surface radius: single standard for all page shells */
  pageRadius: "rounded-[var(--radius-lg)]",

  /** Page surface border: consistent border for page frames */
  pageBorder: "border border-border/50",

  /** Combined page surface: radius + border + bg for main content frames */
  pageSurface: "rounded-[var(--radius-lg)] border border-[color:var(--surface-border)] bg-card [box-shadow:var(--surface-shadow)]",

  /** Standard card surface — quiet structure from a hairline border. */
  card: "rounded-[var(--radius-sm)] border border-[color:var(--surface-border)] bg-[var(--surface-card)]",

  /** Elevated card — reserved for overlays and genuinely layered content. */
  cardElevated: "rounded-[var(--radius-md)] border border-[color:var(--surface-border)] bg-[var(--surface-card)] [box-shadow:var(--depth-1-shadow)]",

  /** Interactive card — state is communicated by tone and border, not motion. */
  cardInteractive:
    "rounded-[var(--radius-sm)] border border-[color:var(--surface-border)] bg-[var(--surface-card)] cursor-pointer hover:border-[color:var(--control-border-hover)] hover:bg-surface-muted/45 active:bg-surface-muted/70 transition-colors duration-150",

  /** Standard gap between elements */
  gap: "gap-4",

  /** Compact gap */
  gapSm: "gap-3",

  /** Large gap */
  gapLg: "gap-6",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// ACCESSIBILITY TOKENS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Focus ring tokens for consistent keyboard navigation styling.
 * Apply to all interactive elements: buttons, links, inputs, tabs.
 *
 * Uses focus-visible (not focus) to only show for keyboard navigation.
 */
export const A11Y = {
  /** Standard focus ring for light/dark mode */
  focusRing:
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card",

  /** Focus ring without offset (for inline elements) */
  focusRingInline:
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",

  /** Skip link styling (for screen reader users) */
  skipLink:
    "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded-lg",
} as const;

/**
 * Spacing tokens for vertical rhythm.
 *
 * | Context        | Token        | Value  |
 * |----------------|--------------|--------|
 * | Operator pages | stack        | gap-4  |
 * | Document pages | stackRelaxed | gap-6  |
 * | Compact UIs    | stackCompact | gap-3  |
 */
export const SPACING = {
  /** Standard content stack: space-y-4 */
  stack: "space-y-4",

  /** Relaxed content stack: space-y-6 */
  stackRelaxed: "space-y-6",

  /** Compact content stack: space-y-3 */
  stackCompact: "space-y-3",

  /** Section spacing: space-y-8 */
  section: "space-y-8",

  /** Inline element gap */
  inline: "gap-2",

  /** Inline element gap (wider) */
  inlineWide: "gap-3",
} as const;

/**
 * Command bar tokens for consistent toolbar styling.
 */
export const COMMAND_BAR = {
  /** Standard command bar container */
  container: "flex items-center gap-3 py-3",

  /** Command bar with bottom border */
  containerBordered: "flex items-center gap-3 py-3 border-b border-border/60",

  /** Filter button base style */
  filterButton:
    "flex h-9 items-center gap-2 rounded-[var(--radius-sm)] border border-[color:var(--control-border)] bg-background px-3 text-sm hover:border-[color:var(--control-border-hover)] hover:bg-surface-muted/45 transition-colors duration-150",

  /** Active filter button */
  filterButtonActive:
    "flex h-9 items-center gap-2 rounded-[var(--radius-sm)] border border-accent-primary/35 bg-accent-primary/8 px-3 text-sm text-accent-primary",

  /** Search input container */
  search: "relative flex-1 max-w-xs",

  /** Search input field */
  searchInput:
    "w-full pl-9 pr-3 py-2 rounded-[var(--radius-sm)] border border-border/60 bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-[border-color,box-shadow] duration-150",

  /** Shared subtle action button (Export, Refresh, etc.) */
  actionButton:
    "inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
} as const;

/**
 * Page header tokens for consistent header styling.
 */
export const PAGE_HEADER = {
  /** Standard page header container */
  container: "flex items-center justify-between gap-4 pb-4",

  /** Page header with bottom border */
  containerBordered:
    "flex items-center justify-between gap-4 pb-4 border-b border-border/60",

  /** Title group (icon + text) */
  titleGroup: "flex items-center gap-3",

  /** Icon container */
  iconContainer:
    "w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center",

  /** Page title */
  title: "text-xl font-semibold text-foreground",

  /** Page subtitle/description */
  subtitle: "text-sm text-muted-foreground",

  /** Actions container */
  actions: "flex items-center gap-2",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY TOKENS — Consistent Text Styles
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Semantic typography tokens for consistent text styling.
 * Use these instead of raw Tailwind text classes.
 *
 * | Token         | Size    | Weight    | Use Case                    |
 * |---------------|---------|-----------|------------------------------|
 * | pageTitle     | text-lg | semibold  | Main page headers           |
 * | sectionTitle  | text-base | semibold | Section headers            |
 * | cardTitle     | text-sm | semibold  | Card/panel headers          |
 * | label         | text-sm | medium    | Form labels, metadata keys  |
 * | body          | text-sm | normal    | Body text, descriptions     |
 * | caption       | text-xs | medium    | Helper text, timestamps     |
 * | stat          | text-2xl | bold     | KPI/metric values           |
 */
export const TEXT = {
  /** Page title: text-xl font-semibold */
  pageTitle: "text-xl font-semibold text-foreground",

  /** Section title: text-base font-semibold */
  sectionTitle: "text-base font-semibold text-foreground",

  /** Card/panel title: text-sm font-semibold */
  cardTitle: "text-sm font-semibold text-foreground",

  /** Form label: text-sm font-medium */
  label: "text-sm font-medium text-foreground",

  /** Body text: text-sm */
  body: "text-sm text-foreground",

  /** Muted body text */
  bodyMuted: "text-sm text-muted-foreground",

  /** Caption/helper text: text-xs */
  caption: "text-xs text-muted-foreground",

  /** Uppercase label: text-xs uppercase tracking */
  labelUppercase:
    "ax-section-labelr text-muted-foreground",

  /** KPI/stat value: text-2xl font-bold */
  stat: "text-2xl font-bold text-foreground tabular-nums",

  /** Smaller stat: text-xl font-semibold */
  statSmall: "text-xl font-semibold text-foreground tabular-nums",

  /** Link style */
  link: "text-sm font-medium text-primary hover:text-primary/80 transition-colors",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// ICON SIZE TOKENS — Consistent Icon Dimensions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Standard icon sizes for consistent visual hierarchy.
 *
 * | Token   | Size   | Use Case                           |
 * |---------|--------|-------------------------------------|
 * | xs      | 12px   | Inline indicators (dots, arrows)   |
 * | sm      | 16px   | Button icons, inline actions        |
 * | md      | 20px   | Default menu/nav icons             |
 * | lg      | 24px   | Feature icons, empty states         |
 * | xl      | 32px   | Hero icons, large empty states      |
 */
export const ICON = {
  /** Extra small: 12px (w-3 h-3) — micro indicators */
  xs: "w-3 h-3",

  /** Small: 16px (w-4 h-4) — buttons, inline, tables (DEFAULT) */
  sm: "w-4 h-4",

  /** Medium: 20px (w-5 h-5) — sidebar baseline */
  md: "w-5 h-5",

  /** Large: 24px (w-6 h-6) — section headers, emphasised */
  lg: "w-6 h-6",

  /** Extra large: 28px (w-7 h-7) — empty states, feature highlights */
  xl: "w-7 h-7",

  /** Page header icon container */
  headerContainer: "w-10 h-10 rounded-2xl flex items-center justify-center",

  /** Page header icon with primary tint */
  headerPrimary:
    "w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// ICON BADGE TOKENS — Pastel Container Governance
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Badge container size + intent tokens consumed by IconBadge.tsx.
 *
 * ⚠️  Badge backgrounds are restricted to:
 *     File cards · KPI cards · Feature tiles · Empty states
 *
 * They must NOT be used in:
 *     Sidebar · Table rows · Toolbars · Dropdowns · Inputs
 */
export const ICON_BADGE = {
  /** Container sizes */
  size: {
    sm: "w-6 h-6 rounded-full",
    md: "w-8 h-8 rounded-sm",
    lg: "w-10 h-10 rounded-2xl",
    xl: "w-12 h-12 rounded-2xl",
  },

  /** Intent → bg + text colour pairings (semantic tokens; no dark: forks) */
  intent: {
    primary: "bg-accent-primary/10 text-accent-primary",
    success: "bg-status-success/10 text-status-success-foreground",
    warning: "bg-status-warning/10 text-status-warning-foreground",
    danger: "bg-status-danger/10 text-status-danger-foreground",
    info: "bg-status-info/10 text-status-info-foreground",
    neutral: "bg-surface-muted text-text-secondary",
  },
} as const;

/**
 * Semantic tone surfaces for chips, decision badges, and validation rows.
 * Prefer these over emerald/amber/sky + dark: pairs at feature call-sites.
 */
export const TONE_SURFACE = {
  success: {
    soft: "bg-status-success/10",
    text: "text-status-success-foreground",
    chip: "bg-status-success/10 text-status-success-foreground",
    bordered: "bg-status-success/10 text-status-success-foreground border-status-success/30",
    border: "border-status-success/30",
  },
  warning: {
    soft: "bg-status-warning/10",
    text: "text-status-warning-foreground",
    chip: "bg-status-warning/10 text-status-warning-foreground",
    bordered: "bg-status-warning/10 text-status-warning-foreground border-status-warning/30",
    border: "border-status-warning/30",
  },
  danger: {
    soft: "bg-status-danger/10",
    text: "text-status-danger-foreground",
    chip: "bg-status-danger/10 text-status-danger-foreground",
    bordered: "bg-status-danger/10 text-status-danger-foreground border-status-danger/30",
    border: "border-status-danger/30",
  },
  info: {
    soft: "bg-status-info/10",
    text: "text-status-info-foreground",
    chip: "bg-status-info/10 text-status-info-foreground",
    bordered: "bg-status-info/10 text-status-info-foreground border-status-info/30",
    border: "border-status-info/30",
  },
  caution: {
    soft: "bg-status-caution/10",
    text: "text-status-caution-foreground",
    chip: "bg-status-caution/10 text-status-caution-foreground",
    bordered: "bg-status-caution/10 text-status-caution-foreground border-status-caution/30",
    border: "border-status-caution/30",
  },
  primary: {
    soft: "bg-accent-primary/10",
    text: "text-accent-primary",
    chip: "bg-accent-primary/10 text-accent-primary",
    bordered: "bg-accent-primary/10 text-accent-primary border-accent-primary/30",
    border: "border-accent-primary/30",
  },
  neutral: {
    soft: "bg-surface-muted",
    text: "text-text-muted",
    chip: "bg-surface-muted text-text-muted",
    bordered: "bg-surface-muted text-text-muted border-border-subtle",
    border: "border-border-subtle",
  },
} as const;
